/* Triage Centre
   Moved from Dashboard.jsx (previously the sole /dashboard page) per
   AbdulRafiu's confirmed structure: /dashboard/triage now owns everything
   triage-oriented. Only the header label changed (was "CLINICAL CONTROL");
   all data logic, wiring, and child components are identical to what
   Dashboard.jsx already had — AlertFilter, PatientList, Hospital Summary
   (fed from PatientList's own onStatsUpdate, unchanged), selected-patient
   Vitals/Device panels, AI Risk/Explainability/Recommendation panels, ML
   Feedback loop, and Ask iVivaCore AI are all untouched, just relocated.

   Added 02/09/2026: AlertCenter mounted per AbdulRafiu's Q3 decision on
   the Patient Safety Actions authorisation — "The primary operational
   AlertCenter.jsx should be mounted in the Triage Centre." Placed as its
   own full-width section above the existing AlertFilter/PatientList grid;
   nothing below it changed.
*/
import React, { useState } from "react";
import { motion } from "framer-motion";
import * as FiIcons from "react-icons/fi";
const { FiActivity, FiTrendingUp } = FiIcons;
import SafeIcon from "../common/SafeIcon";
import PatientList from "../components/PatientList";
import VitalsMonitor from "../components/VitalsMonitor";
import DeviceSourcePanel from "../components/DeviceSourcePanel";
import AlertFilter from "../components/AlertFilter";
import AgentReviewPanel from "../components/AgentReviewPanel";
import HospitalSummaryCard from "../components/dashboard/HospitalSummaryCard";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import toast from "react-hot-toast";
import AskIVivaCoreAI from "../components/dashboard/AskvivacoreReal";
import AIRiskPanel from "../components/dashboard/Airiskpanel";
import ExplainabilityPanel from "../components/dashboard/Explainabilitypanel";
import RecommendationPanel from "../components/dashboard/Recommendationpanel";
import AlertCenter from "../components/AlertCenter";

const TriageCenter = () => {
  const { user } = useAuth();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [anomalyFeedback, setAnomalyFeedback] = useState({
    label: "",
    notes: "",
  });

  // Real stats from PatientList's own onStatsUpdate callback — unchanged
  // from Dashboard.jsx. Feeds HospitalSummaryCard below.
  const [patientStats, setPatientStats] = useState({
    totalPatients: 0,
    activeAlerts: 0,
    criticalAlerts: 0,
  });

  // AlertFilter's activeFilter/onFilterChange — unchanged from
  // Dashboard.jsx. See src/contracts/alertFeed.js for the documented future
  // contract that will let this actually filter PatientList by condition
  // category once a realtime, condition-tagged alerts pipeline exists.
  const [alertCategoryFilter, setAlertCategoryFilter] = useState("all");

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    if (patient?.id) {
      localStorage.setItem("simulatorPatientId", String(patient.id));
      window.dispatchEvent(
        new CustomEvent("simulatorPatientChanged", {
          detail: { patientId: String(patient.id) },
        }),
      );
    }
  };

  const handleAnomalyFeedback = async (stateId) => {
    try {
      await api.post("/geofence/feedback", {
        stateId,
        label: anomalyFeedback.label,
        notes: anomalyFeedback.notes,
      });
      toast.success("ML Feedback recorded");
      setAnomalyFeedback({ label: "", notes: "" });
    } catch (err) {
      toast.error("Failed to submit feedback");
    }
  };

  return (
    <div className="min-h-screen text-slate-200">
      <div className="max-w-[1600px] mx-auto p-4 lg:p-6">
        {/* Header */}
        <header className="flex justify-between items-center mb-6 lg:mb-8">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-black tracking-tight">
              TRIAGE CENTRE
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs text-slate-400 font-bold uppercase tracking-widest">
                Tenant: {user?.managed_tenants?.length || 0} Authorized Scopes
              </span>
            </div>
          </div>
        </header>

        {/* Alert Center — primary operational alert workflow (Q3) */}
        <div className="mb-4 lg:mb-6">
          <AlertCenter />
        </div>

        {/* Main layout — flex so PatientList's fixed widths don't break the grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] gap-4 lg:gap-6 items-start">
          {/* Left column — fixed width, shrink-proof */}
          <div className="w-full min-w-0 space-y-4 lg:space-y-6">
            <AlertFilter
              activeFilter={alertCategoryFilter}
              onFilterChange={setAlertCategoryFilter}
            />
            <PatientList
              onSelectPatient={handleSelectPatient}
              selectedPatient={selectedPatient}
              onStatsUpdate={setPatientStats}
            />
          </div>

          {/* Right column — takes remaining space, won't overflow left */}
          <div className="w-full min-w-0 space-y-4 lg:space-y-6">
            {selectedPatient ? (
              <div className="bg-slate-900/50 border border-slate-800 rounded-3xl p-4 lg:p-6 backdrop-blur-xl">
                {/* Patient header */}
                <div className="flex items-center justify-between mb-4 lg:mb-6">
                  <div className="flex items-center gap-3 lg:gap-4">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <SafeIcon
                        icon={FiActivity}
                        className="text-xl lg:text-2xl"
                      />
                    </div>
                    <div className="min-w-0">
                      {/* PatientList mock data uses `name`; real Supabase users have `email` */}
                      <h2 className="text-base lg:text-xl font-bold truncate">
                        {selectedPatient.name ||
                          selectedPatient.email ||
                          `Patient ${selectedPatient.id}`}
                      </h2>
                      <p className="text-xs text-slate-400">
                        ID: {selectedPatient.id}
                        {selectedPatient.age
                          ? ` • Age ${selectedPatient.age}`
                          : ""}
                        {selectedPatient.condition
                          ? ` • ${selectedPatient.condition}`
                          : ""}
                      </p>
                    </div>
                  </div>
                  {/* Back button on mobile */}
                  <button
                    className="lg:hidden text-xs text-slate-400 hover:text-slate-200 border border-slate-700 px-3 py-1.5 rounded-lg"
                    onClick={() => setSelectedPatient(null)}
                  >
                    ← Back
                  </button>
                </div>

                {/* Device Source Panel — sits above VitalsMonitor */}
                <DeviceSourcePanel patient={selectedPatient} />

                {/* Vitals Monitor */}
                <VitalsMonitor patient={selectedPatient} />

                {/* AI Risk / Explainability / Recommended Actions — placeholder
                    data until Saheed's risk pipeline and Dr. Abdullah's
                    explainability output are connected for this patient */}
                <div className="mt-6 lg:mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                  <AIRiskPanel />
                  <ExplainabilityPanel />
                  <div className="md:col-span-2">
                    <RecommendationPanel />
                  </div>
                </div>

                {/* ML Feedback */}
                <div className="mt-6 lg:mt-8 p-4 lg:p-6 bg-blue-600/5 border border-blue-500/20 rounded-2xl">
                  <h3 className="text-sm font-bold flex items-center gap-2 mb-4">
                    <SafeIcon icon={FiTrendingUp} className="text-blue-400" />
                    ML Engine Feedback Loop
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                    <select
                      className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-sm"
                      value={anomalyFeedback.label}
                      onChange={(e) =>
                        setAnomalyFeedback({
                          ...anomalyFeedback,
                          label: e.target.value,
                        })
                      }
                    >
                      <option value="">Label Recent Anomalies...</option>
                      <option value="true_positive">
                        True Positive (Correct Detection)
                      </option>
                      <option value="false_positive">
                        False Positive (Normal Behavior)
                      </option>
                    </select>
                    <button
                      onClick={() =>
                        handleAnomalyFeedback(selectedPatient.last_anomaly_id)
                      }
                      className="bg-blue-600 px-6 py-2 rounded-xl font-bold hover:bg-blue-500 transition-all text-sm"
                    >
                      Submit Feedback
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <AgentReviewPanel />
            )}
          </div>
        </div>
      </div>

      <AskIVivaCoreAI role="clinician" />
    </div>
  );
};

export default TriageCenter;